import { Activity, FileText, MapPin, Monitor, Settings } from 'lucide-react';
import { Fragment, useMemo } from 'react';
import { FormattedDate } from 'react-intl';

import { ALERT_SEVERITY_ICON_SLOT } from '../../constants/device-alerts';
import { useIntl } from '../../hooks';
import { useLiveActiveSuppressions } from '../../hooks/useLiveActiveSuppressions';
import { useLiveDeviceAlerts } from '../../hooks/useLiveDeviceAlerts';
import { highestDeviceAlertSeverity } from '../../lib/device-alert-text';
import {
  getDeviceStatusBackgroundClass,
  getDeviceStatusBorderClass,
  getDeviceStatusVariant,
} from '../../services/device-status';
import { type Device, DeviceStatus, SuppressionMode } from '../../types';
import DeviceSettingsDialog from '../device-settings/DeviceSettingsDialog';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '../ui';
import { ActiveSuppressionIndicator } from './ActiveSuppressionIndicator';
import { DeviceAlertIndicator } from './DeviceAlertIndicator';
import { type DeviceStatusIconEntry, DeviceStatusIconZone } from './DeviceStatusIconZone';

interface DeviceCardProps {
  device: Device;
  isAdmin: boolean;
  canAccessSettings?: boolean;
  canAcknowledgeAlerts?: boolean;
  showObserverView?: boolean;
  onSelectDevice?: (deviceId: string) => void;
  selectedDeviceId?: string | null;
  setControlModalDeviceId: (deviceId: string | null) => void;
  setEventsModalDeviceId: (deviceId: string | null) => void;
  setSettingsDeviceId: (deviceId: string | null) => void;
  setTelemetryModalDeviceId: (deviceId: string | null) => void;
  settingsDeviceId?: string | null;
  suppressDialogDeviceId?: string | null;
}

export default function DeviceCard({
  device,
  settingsDeviceId,
  selectedDeviceId,
  isAdmin = false,
  canAccessSettings = false,
  canAcknowledgeAlerts = false,
  showObserverView = false,
  onSelectDevice,
  setSettingsDeviceId,
  setControlModalDeviceId,
  setEventsModalDeviceId,
  setTelemetryModalDeviceId,
}: DeviceCardProps) {
  const intl = useIntl();
  const isSelected = selectedDeviceId === device.id;
  const isOnline = useMemo(() => {
    return device.status === DeviceStatus.ONLINE;
  }, [device.status]);
  const txDisplayStatus = useMemo(() => {
    return isOnline && device.txEnabled;
  }, [device.txEnabled, isOnline]);
  const liveActiveSuppressions = useLiveActiveSuppressions(device.id);
  // The one place expired alerts are dropped: the indicator and popover render only this list.
  const liveAlerts = useLiveDeviceAlerts(device.alerts);
  const statusIcons = useMemo<DeviceStatusIconEntry[]>(() => {
    const icons: DeviceStatusIconEntry[] = [];

    if (liveActiveSuppressions.length > 0) {
      icons.push({
        key: 'active-suppressions',
        severity: isOnline ? 'activeSuppression' : 'offlineSuppression',
        render: ({ animated }) => (
          <ActiveSuppressionIndicator
            records={liveActiveSuppressions}
            isOnline={isOnline}
            animated={animated}
          />
        ),
      });
    }

    if (liveAlerts.length > 0) {
      icons.push({
        key: 'device-alerts',
        severity: ALERT_SEVERITY_ICON_SLOT[highestDeviceAlertSeverity(liveAlerts)],
        render: ({ animated }) => (
          <DeviceAlertIndicator
            deviceId={device.id}
            deviceSerial={device.serial}
            alerts={liveAlerts}
            canAcknowledge={canAcknowledgeAlerts}
            animated={animated}
          />
        ),
      });
    }

    return icons;
  }, [
    canAcknowledgeAlerts,
    device.id,
    device.serial,
    isOnline,
    liveActiveSuppressions,
    liveAlerts,
  ]);
  const hasLocation = device.settings?.latitude != null && device.settings?.longitude != null;
  const isSuppressionDisabled =
    device.settings?.suppressionMode === SuppressionMode.DISABLED || !device.txEnabled;

  const handleOpenEmulator = (serial: string, token: string) => {
    const emulatorUrl = `http://${window.location.hostname}:5174/?serial=${encodeURIComponent(serial)}&token=${encodeURIComponent(token)}`;

    const popup = window.open(
      emulatorUrl,
      `device-emulator-${serial}`,
      'width=800,height=900,scrollbars=no,resizable=no,toolbar=no,menubar=no,location=no,status=no',
    );

    if (!popup) {
      alert(intl.formatMessage({ id: 'deviceCard.popupBlocked' }));
    }
  };

  return (
    <Fragment key={device.id}>
      <Card
        data-test-id="device-card"
        data-device-serial={device.serial}
        className={`relative flex h-full flex-col bg-background-light transition-all duration-200 border-2 ${isOnline && isSuppressionDisabled ? 'border-yellow-dark' : getDeviceStatusBorderClass(device.status)} ${isOnline && isSuppressionDisabled ? 'bg-yellow-light' : getDeviceStatusBackgroundClass(device.status)} ${isSelected ? 'ring-2 ring-blue-500 shadow-lg' : 'hover:shadow-md'}`}
      >
        <div className="flex items-center gap-1 justify-between">
          <DeviceStatusIconZone icons={statusIcons} />
          <div className="flex items-center gap-1">
            {onSelectDevice && hasLocation && (
              <Button
                variant={isSelected ? 'default' : 'ghost'}
                size="icon"
                className="h-8 w-8 text-foreground"
                aria-pressed={isSelected}
                onClick={() => onSelectDevice(device.id)}
                title={intl.formatMessage({
                  id: isSelected ? 'deviceCard.hideOnMap' : 'deviceCard.showOnMap',
                })}
              >
                <MapPin className="h-4 w-4" />
              </Button>
            )}
            {isAdmin && (import.meta.env.DEV || import.meta.env.VITE_EMULATOR === 'true') && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-foreground"
                onClick={() => handleOpenEmulator(device.serial, device.token)}
                title={intl.formatMessage({ id: 'deviceCard.openEmulator' })}
              >
                <Monitor className="h-4 w-4" />
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-foreground"
                onClick={() => setEventsModalDeviceId(device.id)}
                title={intl.formatMessage({ id: 'deviceCard.viewEvents' })}
              >
                <FileText className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-foreground"
              onClick={() => setTelemetryModalDeviceId(device.id)}
              title={intl.formatMessage({ id: 'deviceTelemetry.button' })}
            >
              <Activity className="h-4 w-4" />
            </Button>
            {canAccessSettings && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-foreground"
                onClick={() => setSettingsDeviceId(device.id)}
                title={intl.formatMessage({ id: 'deviceCard.deviceSettings' })}
              >
                <Settings className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        <CardHeader className="pb-3 pt-0">
          <div className="flex items-start">
            <div className="flex-1">
              <CardTitle className="text-base font-medium text-foreground">
                <div className="flex items-center gap-1">
                  <span>
                    {device.serial || intl.formatMessage({ id: 'deviceCard.unknownModel' })}
                  </span>
                  {showObserverView && (
                    <Badge variant="outline" data-test-id="device-observer-pill">
                      {intl.formatMessage({ id: 'deviceCard.observer' })}
                    </Badge>
                  )}
                </div>
              </CardTitle>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex flex-1 flex-col">
          <div className="flex-1 space-y-3">
            <div className="flex justify-around items-center">
              <Badge
                data-test-id="device-status-badge"
                variant={getDeviceStatusVariant(device.status)}
              >
                {intl.formatMessage({
                  id: `map.status.${device.status.toLowerCase()}`,
                })}
              </Badge>
              <Badge variant={txDisplayStatus ? 'success' : 'secondary'}>
                {intl.formatMessage({
                  id: `deviceCard.${txDisplayStatus ? 'txEnabled' : 'txDisabled'}`,
                })}
              </Badge>
            </div>
            <dl>
              {isAdmin && (
                <>
                  <dd className="text-sm font-bold text-foreground">
                    {intl.formatMessage({ id: 'deviceCard.firmware' })}
                  </dd>
                  <dt className="pl-4 text-sm font-normal text-foreground">
                    {device.firmwareVersion}
                  </dt>
                </>
              )}

              <dd className="text-sm font-bold text-foreground">
                {intl.formatMessage({ id: 'detections.suppressionMode' })}
              </dd>
              <dt className="pl-4 text-sm font-normal text-foreground">
                {intl.formatMessage({
                  id: `deviceSettings.fields.suppressionMode.${device.settings?.suppressionMode}.label`,
                })}
              </dt>

              {device.lastSeenAt && (
                <>
                  <dd className="text-sm font-bold text-foreground">
                    {intl.formatMessage({ id: 'deviceCard.lastSeen' })}
                  </dd>
                  <dt className="pl-4 text-sm font-normal text-foreground">
                    <FormattedDate
                      value={new Date(device.lastSeenAt)}
                      year="numeric"
                      month="numeric"
                      day="numeric"
                      hour="2-digit"
                      minute="2-digit"
                      second="2-digit"
                    />
                  </dt>
                </>
              )}
            </dl>
          </div>
        </CardContent>

        <div className="flex w-full">
          <Button
            variant="outline"
            className="mt-4 justify-center w-full gap-2"
            onClick={() => setControlModalDeviceId(device.id)}
          >
            {intl.formatMessage({
              id: showObserverView ? 'deviceCard.details' : 'deviceCard.control',
            })}
            {device.activeDetectionGroupsCount > 0 && (
              <Badge variant="destructive" className="ml-1 px-2 py-0.5 text-xs">
                {device.activeDetectionGroupsCount}
              </Badge>
            )}
          </Button>
        </div>
      </Card>

      {canAccessSettings && settingsDeviceId === device.id && (
        <DeviceSettingsDialog device={device} onOpenChange={() => setSettingsDeviceId(null)} />
      )}
    </Fragment>
  );
}
